import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { appServicesAPI, clustersAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useCluster } from '../../context/ClusterContext';
import ActionsMenu, { MENU_ITEM } from '../../components/ActionsMenu';

const CRASH_REASONS = ['CrashLoopBackOff','OOMKilled','Error','ImagePullBackOff','ErrImagePull'];
const STATUS_COLOR  = { running:'var(--color-success-500)', pending:'var(--color-warning-500)', failed:'var(--color-danger-500)',
  CrashLoopBackOff:'var(--color-danger-500)', OOMKilled:'var(--color-danger-500)', ImagePullBackOff:'var(--color-danger-500)', Error:'var(--color-danger-500)' };

function StatusDot({ status, reason, verifying }) {
  // Two-phase load: phase 1 (live=false) shows the STORED status instantly; phase 2
  // (live=true) reconciles against the cluster and may correct it to "Unreachable".
  // While the live reconcile is still in flight, show a neutral "checking…" rather
  // than a confident stored status that could flip a moment later.
  if (verifying) {
    return (
      <span title="Verifying live status…"
        style={{ display:'inline-flex', alignItems:'center', gap:5, background:'var(--muted-2)18',
        color:'var(--muted-2)', padding:'2px 10px', borderRadius:20, fontSize:11, fontWeight:700 }}>
        <span style={{ width:6,height:6,borderRadius:'50%',background:'var(--muted-2)' }} />
        checking…
      </span>
    );
  }
  // 'unknown' = the cluster agent is offline, so live status can't be verified.
  const isUnreachable = (status||'').toLowerCase() === 'unknown';
  const key = reason && CRASH_REASONS.includes(reason) ? reason : (status||'unknown').toLowerCase();
  const color = STATUS_COLOR[key] || 'var(--muted-2)';
  return (
    <span title={isUnreachable ? "Agent offline — live status can't be verified" : undefined}
      style={{ display:'inline-flex', alignItems:'center', gap:5, background:color+'18',
      color, padding:'2px 10px', borderRadius:20, fontSize:11, fontWeight:700 }}>
      <span style={{ width:6,height:6,borderRadius:'50%',background:color }} />
      {isUnreachable ? 'Unreachable' : (reason || status || 'unknown')}
    </span>
  );
}

function Age({ ts }) {
  if (!ts) return <span style={{ color:'var(--muted-2)' }}>—</span>;
  const s = Math.floor((Date.now()-new Date(ts).getTime())/1000);
  if (s<60) return <span>{s}s</span>;
  if (s<3600) return <span>{Math.floor(s/60)}m</span>;
  if (s<86400) return <span>{Math.floor(s/3600)}h</span>;
  return <span>{Math.floor(s/86400)}d</span>;
}

export default function AppServicesListPage() {
  const { hasSectionAction } = useAuth();
  const { selectedCluster } = useCluster();   // scope the list to the ACTIVE cluster
  const canCreate = hasSectionAction('workloads', 'create');
  const canEdit   = hasSectionAction('workloads', 'edit');
  const canDelete = hasSectionAction('workloads', 'delete');
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [clusters, setClusters] = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [reconciling, setReconciling] = useState(false); // background live-status refresh
  const [reconciledOnce, setReconciledOnce] = useState(false); // has phase-2 completed at least once?
  const [filter,   setFilter]   = useState('');
  // A dropdown filter, not a drill-down grid. A namespace-per-card grid stopped scaling
  // once a cluster accumulated many single-workload namespaces (e.g. QA test runs): every
  // namespace, no matter how trivial, demanded its own card AND an extra click before
  // the actual table of workloads ever appeared.
  const [nsFilter, setNsFilter] = useState('');

  // 'Pod' included — the standalone "My Pods" list page was folded into this table
  // (reported live: a near-empty, rarely-used page that only ever showed bare-Pod
  // deploys) rather than kept as its own nav item. A Pod-kind row renders identically
  // to any other here; its Kind badge just reads "Pod" instead of "StatefulSet" etc.
  const WORKLOAD_KINDS = ['Deployment', 'StatefulSet', 'raw', 'DaemonSet', 'Job', 'CronJob', 'Pod'];
  // SarvKube-MANAGED workloads only — rows this app created (have a DB id → full
  // Open/Scale/Restart/Delete). We deliberately DO NOT merge live cluster workloads
  // here: showing cert-manager / kube-system / Helm / other-teams' workloads would
  // let a user act on cluster infrastructure they didn't create (blast-radius risk).
  const _tracked = (rows) => (rows||[]).filter(s => WORKLOAD_KINDS.includes(s.kind));

  // Two-phase load: phase 1 renders DB rows instantly
  // (live=false, ~50ms); phase 2 reconciles real cluster status in the background
  // (live=true — can be slow / block on a dead-agent cluster). Previously this awaited
  // the live=true call before rendering ANYTHING, so a single unreachable cluster froze
  // the whole Workloads page with "0/0 workloads".
  const loadSeq = useRef(0);   // newest load wins — a cluster switch mid-load discards the old one
  const load = useCallback(async () => {
    const seq = ++loadSeq.current;
    // Scope to the ACTIVE cluster (cluster_id=undefined fetched ALL clusters org-wide
    // → other clusters' workloads, incl. i4c, showed under the selected cluster).
    const cid = selectedCluster?.id;
    setLoading(true);
    setReconciledOnce(false);   // cluster switch / reload → statuses unverified again → show "checking…"
    try {
      const [sR,cR] = await Promise.all([
        appServicesAPI.list(cid, false),   // live=false → instant DB rows, cluster-scoped
        clustersAPI.list(),
      ]);
      setClusters(cR.data||[]);
      setServices(_tracked(sR.data));
    } catch (e) {}
    finally { setLoading(false); }

    // Phase 2: background live-status reconcile (don't block the render).
    setReconciling(true);
    try {
      const lR = await appServicesAPI.list(cid, true);   // live=true, cluster-scoped
      setServices(_tracked(lR.data));
      setReconciledOnce(true);   // live status now verified → stop showing "checking…"
      // The API refreshes its per-namespace pod snapshot in the background, so a namespace
      // seen for the first time has no live pods yet. One follow-up fetch picks them up.
      if ((lR.data || []).some(s => !(s.k8s_status?.pods || []).length)) {
        await new Promise(r => setTimeout(r, 4000));
        const again = await appServicesAPI.list(cid, true);
        if (seq === loadSeq.current) setServices(_tracked(again.data));
      }
    } catch (e) { /* keep the DB-row view if reconcile fails/times out */ }
    finally { setReconciling(false); }
  }, [selectedCluster]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  // Reset the namespace filter on an actual CLUSTER SWITCH — a namespace name from the
  // previous cluster is meaningless (and usually just empty) on the new one.
  const prevClusterId = useRef(selectedCluster?.id);
  useEffect(() => {
    if (prevClusterId.current !== undefined && prevClusterId.current !== selectedCluster?.id) {
      setNsFilter('');
    }
    prevClusterId.current = selectedCluster?.id;
  }, [selectedCluster]);

  const handleDelete = async (id, name, e) => {
    e.stopPropagation();
    if (!window.confirm(`Delete "${name}"? This removes the K8s deployment and all associated resources.`)) return;
    try { await appServicesAPI.delete(id); setServices(p=>p.filter(s=>s.id!==id)); }
    catch (err) { alert(err.response?.data?.detail||'Delete failed'); }
  };

  const clusterName = id => clusters.find(c=>c.id===id)?.name||`cluster-${id}`;
  const namespaceNames = [...new Set(services.map(s => s.namespace).filter(Boolean))].sort();
  const filtered = services.filter(s =>
    (!filter || s.name.toLowerCase().includes(filter.toLowerCase()) ||
      (s.image||'').toLowerCase().includes(filter.toLowerCase())) &&
    (!nsFilter || s.namespace === nsFilter)
  );

  const kindIcon = k => k==='StatefulSet' ? '🗄️' : k==='raw' ? '📄' : k==='Pod' ? '📦' : '🚀';

  return (
    <div style={{ padding:'24px 32px' }}>
      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:24 }}>
        <div>
          <h1 style={{ margin:0, fontSize:22, fontWeight:700, color:'var(--ink)' }}>My Workloads</h1>
          <p style={{ margin:'4px 0 0', fontSize:13, color:'var(--muted)' }}>
            Deployments &amp; StatefulSets deployed here, through SarvKube — not every workload
            on the cluster. To browse <em>everything</em>, including resources others created
            directly, use Workloads in the sidebar.
          </p>
        </div>
        {canCreate && (
        <button onClick={() => navigate('/compute/workloads/new')} className="btn btn-primary">
          + Deploy Service
        </button>
        )}
      </div>

      {/* Filters — search + namespace dropdown + refresh, always visible, no drill-down
          click needed first. */}
      <div style={{ display:'flex', gap:10, marginBottom:16, alignItems:'center' }}>
        <input placeholder="Search by name or image…" value={filter}
          onChange={e => setFilter(e.target.value)}
          className="input" style={{ width:260 }} />
        <select value={nsFilter} onChange={e => setNsFilter(e.target.value)}
          className="select" style={{ width:'auto' }}>
          <option value="">All namespaces</option>
          {namespaceNames.map(n => <option key={n} value={n}>{n}</option>)}
        </select>
        <button onClick={load} disabled={loading} className="btn btn-secondary btn-sm">
          {loading ? '…' : '⟳ Refresh'}
        </button>
        <span style={{ marginLeft:'auto', fontSize:12, color:'var(--muted-2)' }}>
          {filtered.length} / {services.length} workloads
          {reconciling && <span style={{ marginLeft:8, color:'var(--brand)' }}>⟳ refreshing status…</span>}
        </span>
      </div>

      {!loading && services.length === 0 ? (
        <div className="card" style={{ textAlign:'center', padding:'80px 20px' }}>
          <div style={{ fontSize:48, marginBottom:12 }}>🔧</div>
          <div style={{ fontSize:16, fontWeight:600, color:'var(--ink-2)', marginBottom:8 }}>No workloads deployed yet</div>
          <div style={{ fontSize:13, color:'var(--muted-2)', marginBottom:20 }}>
            Deploy a backend service, Elasticsearch, Redis, or any containerised workload.
          </div>
          {canCreate && (
          <button onClick={() => navigate('/compute/workloads/new')} className="btn btn-primary">
            + Deploy Service
          </button>
          )}
        </div>
      ) : (
        <div className="table-card">
          <table className="ds">
            <thead>
              <tr>
                {['Workload','Kind','Image','Cluster / NS','Status','Port','Ingress','Age',''].map(h=>(
                  <th key={h}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(svc => {
                // The API already returns k8s_status as an object; JSON.parse on an object
                // throws, which silently emptied the pod list (and crash detection) here.
                const k8s  = typeof svc.k8s_status === 'string'
                  ? (() => { try{return JSON.parse(svc.k8s_status);}catch{return {};} })()
                  : (svc.k8s_status || {});
                const pods = k8s.pods||[];
                const cr   = pods.find(p=>p.reason&&CRASH_REASONS.includes(p.reason))?.reason;
                // Deployed via App Catalog (redis/postgres/mongo/elasticsearch topologies) carry
                // this breadcrumb in `notes` — same marker catalog.py's own release parsing uses.
                // These get the richer catalog detail page (connection info, bootstrap history,
                // keystore) instead of the generic workload page.
                const isCatalog = String(svc.notes||'').startsWith('Catalog:');
                return (
                  <tr key={svc.id}
                    onClick={()=>navigate(isCatalog ? `/catalog/${svc.id}` : `/compute/workloads/${svc.id}`)}
                    style={{ cursor:'pointer' }}>
                    <td>
                      <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                        <span style={{ fontSize:18 }}>{kindIcon(svc.kind)}</span>
                        <div>
                          <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                            <span style={{ fontWeight:600, color:'var(--ink)', fontSize:13 }}>{svc.display_name||svc.name}</span>
                            {isCatalog && (
                              <span style={{ fontSize:9.5, fontWeight:700, letterSpacing:0.3, textTransform:'uppercase',
                                padding:'1px 6px', borderRadius:20, background:'rgba(37,99,235,0.12)', color:'var(--brand)' }}
                                title="Deployed via App Catalog">via App Catalog</span>
                            )}
                          </div>
                          {pods.length > 0 ? (
                            <div style={{ fontSize:11, color:'var(--muted-2)', marginTop:1, fontFamily:'monospace' }}
                              title={pods.map(p=>p.name).join(', ')}>
                              {pods.length === 1
                                ? pods[0].name
                                : `${pods[0].name} +${pods.length-1} more`}
                            </div>
                          ) : (
                            <div style={{ fontSize:11, color:'var(--muted-2)', marginTop:1 }}>{svc.replicas} replica{svc.replicas!==1?'s':''}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className="badge">{svc.kind}</span>
                    </td>
                    <td>
                      <code style={{ fontSize:11, color:'var(--ink-2)', background:'var(--surface-2)',
                        padding:'2px 6px', borderRadius:4, maxWidth:180, display:'block',
                        overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}
                        title={svc.image}>{svc.image}</code>
                    </td>
                    <td style={{ fontSize:12, color:'var(--ink-2)' }}>
                      <div>{clusterName(svc.cluster_id)}</div>
                      <div style={{ fontFamily:'monospace', fontSize:11, color:'var(--muted-2)', marginTop:1 }}>{svc.namespace}</div>
                    </td>
                    <td>
                      <StatusDot status={svc.status} reason={cr}
                        verifying={reconciling && !reconciledOnce} />
                    </td>
                    <td style={{ fontSize:12, color:'var(--ink-2)' }}>
                      {svc.port ? <code style={{ fontSize:11 }}>{svc.port}/{svc.service_type||'ClusterIP'}</code> : '—'}
                    </td>
                    <td style={{ fontSize:12 }}>
                      {svc.subdomain
                        ? <span style={{ color:'var(--brand)', fontSize:11 }}>🌐 {svc.subdomain}</span>
                        : <span style={{ color:'var(--muted-2)' }}>—</span>}
                    </td>
                    <td style={{ fontSize:12, color:'var(--muted-2)' }}>
                      <Age ts={svc.created_at} />
                    </td>
                    <td>
                      {svc._live ? (
                        <span style={{ padding:'4px 10px', fontSize:11, color:'var(--muted-2)' }}
                          title="Live cluster workload (not managed by SarvKube)">live</span>
                      ) : (
                        <ActionsMenu>
                          <button style={MENU_ITEM}
                            onClick={()=>navigate(isCatalog ? `/catalog/${svc.id}` : `/compute/workloads/${svc.id}`)}>
                            Open
                          </button>
                          {/* Catalog releases have no YAML tab (CatalogReleaseDetailPage.js) —
                              this deep-links straight to the one WorkloadDetailPage.js has. */}
                          {canEdit && !isCatalog && (
                            <button style={MENU_ITEM}
                              onClick={()=>navigate(`/compute/workloads/${svc.id}?tab=yaml`)}>
                              Edit
                            </button>
                          )}
                          {canDelete && (
                            <button style={{ ...MENU_ITEM, color: 'var(--color-danger-500)' }}
                              onClick={e=>handleDelete(svc.id,svc.name,e)}>
                              🗑️ Delete
                            </button>
                          )}
                        </ActionsMenu>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
